import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useTags } from "@/hooks/use-tags";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { useState } from "react";

interface TagsSelectProps {
	value: string[];
	onChange: (value: string[]) => void;
	placeholder?: string;
}

export function TagsSelect({
	value,
	onChange,
	placeholder = "Select tags...",
}: TagsSelectProps) {
	const { data: tags = [], isLoading, refetch } = useTags();
	const [open, setOpen] = useState(false);
	const [newTag, setNewTag] = useState("");
	const [isCreating, setIsCreating] = useState(false);

	if (isLoading) {
		return (
			<div className="h-10 w-full bg-gray-100 animate-pulse rounded-md"></div>
		);
	}

	const handleSelect = (tagId: string) => {
		if (value.includes(tagId)) {
			onChange(value.filter((id) => id !== tagId));
		} else {
			onChange([...value, tagId]);
		}
	};

	const handleRemove = (tagId: string) => {
		onChange(value.filter((id) => id !== tagId));
	};

	const createTag = async () => {
		if (!newTag.trim()) return;

		try {
			setIsCreating(true);

			const { data, error } = await supabase
				.from("tags")
				.insert({ name: newTag.trim() })
				.select()
				.single();

			if (error) {
				if (error.code === "23505") {
					// Unique violation error code
					toast({
						title: "Tag already exists",
						description: "This tag already exists in the database.",
						variant: "destructive",
					});

					// Try to find the existing tag with this name
					const { data: existingData } = await supabase
						.from("tags")
						.select("id")
						.eq("name", newTag.trim())
						.single();

					if (existingData) {
						// Add the existing tag to the selection if it's not already there
						if (!value.includes(existingData.id)) {
							onChange([...value, existingData.id]);
						}
					}
				} else {
					throw error;
				}
			} else if (data) {
				// Add new tag to selection
				onChange([...value, data.id]);
				setNewTag("");
				await refetch();
				toast({
					title: "Tag created",
					description: `"${data.name}" has been added to the tags list.`,
				});
			}
		} catch (err: any) {
			console.error("Error creating tag:", err);
			toast({
				title: "Error creating tag",
				description: err.message || "An unexpected error occurred",
				variant: "destructive",
			});
		} finally {
			setIsCreating(false);
		}
	};

	return (
		<div className="space-y-2">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						className="w-full justify-between">
						{value.length > 0
							? `${value.length} selected`
							: placeholder}
						<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-full p-0">
					<Command>
						<CommandInput
							placeholder="Create or search tags..."
							value={newTag}
							onValueChange={setNewTag}
						/>

						{/* Create tag button */}
						{newTag.trim() &&
							!tags.some(
								(tag) =>
									tag.name.toLowerCase() ===
									newTag.trim().toLowerCase()
							) && (
								<Button
									variant="ghost"
									className="w-full justify-start gap-2 px-2 py-1.5 text-sm"
									onClick={createTag}
									disabled={isCreating}>
									<Plus className="h-4 w-4" />
									Create "{newTag.trim()}"
									{isCreating && (
										<span className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
									)}
								</Button>
							)}

						<CommandEmpty>
							{!newTag.trim()
								? "No tag found."
								: "No tag found. Type to create one."}
						</CommandEmpty>
						<CommandList>
							<CommandGroup>
								{tags?.map((tag) => (
									<CommandItem
										key={tag.id}
										value={tag.name}
										onSelect={() => {
											handleSelect(tag.id);
											setOpen(false);
										}}>
										<Check
											className={cn(
												"mr-2 h-4 w-4",
												value.includes(tag.id)
													? "opacity-100"
													: "opacity-0"
											)}
										/>
										{tag.name}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>

			{value.length > 0 && (
				<div className="flex flex-wrap gap-2 mt-2">
					{value.map((tagId) => {
						const tag = tags?.find((t) => t.id === tagId);
						if (!tag) return null;

						return (
							<Badge
								key={tag.id}
								variant="secondary"
								className="px-2 py-1">
								{tag.name}
								<button
									type="button"
									onClick={() => handleRemove(tag.id)}
									className="ml-1 text-gray-500 hover:text-gray-700">
									<X className="h-3 w-3" />
								</button>
							</Badge>
						);
					})}
				</div>
			)}
		</div>
	);
}
